/**
 * Rooms on a phone and an iPad, in the Season design. A phone is one list with the docked New room; upright shows the
 * rooms as cards; on its side the list stands beside the open room's summary. It reads the same rooms and does the
 * same things as the computer's page.
 */
import { type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { WebinarSummary } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { GoLiveSheet } from "../../components/GoLiveSheet";
import { DockedBar } from "../../components/season/DockedBar";
import { Empty } from "../../components/season/Empty";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { PageHead } from "../../components/season/PageHead";
import { Pane, Panes } from "../../components/season/Panes";
import { Picture } from "../../components/season/Picture";
import { Button } from "../../components/ui/Button";
import { Chip, Pill } from "../../components/ui/Pill";
import { useToast } from "../../components/ui/Toast";
import { InviteSheet, inviteTargetOf } from "../../components/InviteSheet";
import "../../styles/season-rooms.css";
import { NewRoomSheet } from "./NewRoomSheet";
import { copyRoomLink } from "./RoomActions";
import { FILTERS, HeldStrip } from "./roomsParts";
import { KIND_TONE, kindOf, peopleLine, type RoomFilter, rowLine, stateLine, whenLine } from "./roomWords";
import { NowBand, QuickStrip } from "./RoomsNow";
import { RoomMore } from "./SeasonRoomMenu";
import { CountSwitch, LinkRow, RoomPicture, SectionRows } from "./seasonRoomParts";
import { useRoomPage } from "./useRoomPage";
import { useRoomsPage } from "./useRoomsPage";

const n = (v: number) => v.toLocaleString("en-US");

/** The line that says what the room is doing now, coloured as the design colours it. */
function State({ room }: { room: WebinarSummary }) {
  const s = stateLine({ status: room.status, live: room.live, watching: room.watching }, Date.now());
  return s ? <span className={`k-rm rm-st ${s.tone}`}>{s.text}</span> : null;
}

function RoomRow({ room, on, onPick, menu }: { room: WebinarSummary; on?: boolean; onPick?: () => void; menu: ReactNode }) {
  const kind = kindOf(room);
  const draft = kind === "Draft";
  const body = (
    <>
      <Picture size="row" src={draft ? null : room.poster_url} fallback={draft ? "pencil" : "play"} />
      <span className="k-rt">
        <b dir="auto">{room.title}</b>
        <span className="k-rm">
          <Pill tone={KIND_TONE[kind]}>{kind}</Pill>
          {rowLine(room)}
        </span>
        <State room={room} />
      </span>
    </>
  );
  return (
    <li className={on ? "k-row k-on" : "k-row"}>
      {onPick ? (
        <button type="button" className="k-rb" onClick={onPick} aria-current={on ? "true" : undefined}>
          {body}
        </button>
      ) : (
        <Link className="k-rb" to={`/rooms/${room.id}`} data-opens={`webinar:${room.id}`}>
          {body}
        </Link>
      )}
      {menu}
    </li>
  );
}

/** A room as a card (the iPad upright): its picture, its words, three numbers, and Copy link or Continue. */
function RoomCard({ room, layout, onChanged }: { room: WebinarSummary; layout: SeasonLayout; onChanged: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const draft = room.status === "draft";
  return (
    <article className="k-card k-flush rm-card">
      <Link className="rm-cb" to={`/rooms/${room.id}`} data-opens={`webinar:${room.id}`} aria-label={room.title}>
        <RoomPicture room={room} />
        <span className="rm-cbody">
          <b className="rm-ct" dir="auto">{room.title}</b>
          <span className="k-rm">{whenLine(room)}</span>
          {draft ? null : <State room={room} />}
          {draft ? null : (
            <span className="rm-nums">
              <span>
                <b>{n(room.registered)}</b>registered
              </span>
              <span>
                <b>{room.attend_rate}%</b>came
              </span>
              <span>
                <b>{n(room.customers)}</b>became customers
              </span>
            </span>
          )}
        </span>
      </Link>
      <div className="rm-cf">
        {draft ? (
          <Button onClick={() => navigate(boardPath(room.id))}>Continue on the board</Button>
        ) : (
          <Button icon="link" onClick={() => void copyRoomLink(room.register_url, toast)}>
            Copy link
          </Button>
        )}
        <RoomMore room={room} layout={layout} onChanged={onChanged} />
      </div>
    </article>
  );
}

/** The picked room, in the middle pane of the iPad on its side: who it is for, what to do with it, its sections. */
function SideSummary({ room, onChanged }: { room: WebinarSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const draft = room.status === "draft";
  const { detail, lastLive } = useRoomPage(room.id);
  return (
    <div className="rm-sum">
      <RoomPicture room={room} />
      <h2 className="k-h1" dir="auto">{room.title}</h2>
      {draft ? (
        <>
          <div className="rm-btns">
            <Button after="right" onClick={() => navigate(boardPath(room.id))}>
              Continue on the board
            </Button>
            <RoomMore room={room} layout="side" onChanged={onChanged} />
          </div>
        </>
      ) : (
        <>
          <p className="k-meta rm-when">{whenLine(room)}</p>
          <LinkRow url={room.register_url} />
          <div className="rm-btns">
            <Button icon="userplus" onClick={() => setInviting(true)}>
              Invite people
            </Button>
            <Button icon="link" onClick={() => void copyRoomLink(room.register_url, toast)}>
              Copy link
            </Button>
            <Button icon="eye" onClick={() => window.open(room.register_url, "_blank", "noopener")}>
              Preview
            </Button>
            <RoomMore room={room} layout="side" onChanged={onChanged} />
          </div>
          <ul className="k-mets k-3 rm-m3">
            <li className="k-met">
              <b>{n(room.registered)}</b>
              <span>registered</span>
            </li>
            <li className="k-met">
              <b>{room.attend_rate}%</b>
              <span>came</span>
            </li>
            <li className="k-met">
              <b>{n(room.customers)}</b>
              <span>became customers</span>
            </li>
          </ul>
          {detail ? <SectionRows detail={detail} lastLive={lastLive} /> : null}
        </>
      )}
      {inviting ? (
        <InviteSheet
          webinar={inviteTargetOf(room)}
          onClose={() => setInviting(false)}
          onSent={(send) => toast(`Sending ${n(send.total)} ${send.total === 1 ? "invite" : "invites"}`)}
          onToast={toast}
        />
      ) : null}
    </div>
  );
}

export function SeasonRooms({ layout }: { layout: SeasonLayout }) {
  const { hostFirst, data, phase, reload, rooms, quick, band, counts, shown, filter, setFilter, held, billing } = useRoomsPage();
  const navigate = useNavigate();
  const [goingLive, setGoingLive] = useState(false);
  const [making, setMaking] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const goLive = (
    <button type="button" className="btn live rm-gl" disabled={held !== null} onClick={() => setGoingLive(true)}>
      <span className="dot" />
      Go live now
    </button>
  );
  const newRoom = (
    <Button variant="p" icon="plus" disabled={held !== null} onClick={() => setMaking(true)}>
      New room
    </Button>
  );
  const sheets = (
    <>
      {goingLive ? <GoLiveSheet hostFirst={hostFirst} onClose={() => setGoingLive(false)} onMade={reload} /> : null}
      {making ? <NewRoomSheet onClose={() => setMaking(false)} /> : null}
    </>
  );
  const strip = held ? <HeldStrip held={held} billing={billing} /> : null;
  const now = (
    <>
      <NowBand {...band} />
      <QuickStrip calls={quick} />
    </>
  );
  const title = (
    <PageHead title="Rooms" {...(data ? { count: rooms.length, sub: peopleLine(rooms) || undefined } : {})} right={layout === "phone" ? goLive : <div className="rm-hb">{goLive}{newRoom}</div>} />
  );

  if (!data || rooms.length === 0) {
    return (
      <Page layout={layout} narrow className="rm">
        {title}
        {strip}
        {phase === "error" && !data ? (
          <div className="k-card rm-state">
            <span>Your rooms didn&apos;t load.</span>
            <Button onClick={reload}>Try again</Button>
          </div>
        ) : !data ? (
          <p className="k-sub" aria-busy="true">
            Loading
          </p>
        ) : (
          <>
          {now}
          <Empty title="Make your first room">
            <div className="rm-btns">
              <Button variant="p" icon="upload" onClick={() => navigate("/rooms/new?from=recording")}>
                From a recording
              </Button>
              <Button icon="live" onClick={() => navigate("/rooms/new?from=live")}>
                A live room
              </Button>
            </div>
          </Empty>
          </>
        )}
        {sheets}
      </Page>
    );
  }

  const switchOptions = FILTERS.map(([value, label]) => ({ value, label, count: counts[value] }));
  const menuFor = (room: WebinarSummary) => <RoomMore room={room} layout={layout} onChanged={reload} />;

  if (layout === "side") {
    const open = shown.find((r) => r.id === picked) ?? shown[0];
    return (
      <>
        <Panes className="rm-split">
          <Pane kind="list" keep="rooms-list" label="Rooms">
            <div className="k-sp-lh">
              <div className="k-head rm-lh">
                <h1 className="k-title">
                  Rooms <small>{rooms.length}</small>
                </h1>
              </div>
              <div className="rm-lb">
                {goLive}
                {newRoom}
              </div>
            </div>
            <Page layout="side" className="rm rm-list">
              {strip}
              {now}
              <div className="rx-h">
                <h2>Your rooms</h2>
              </div>
              <div className="k-chips rm-chips">
                {FILTERS.map(([k, label]) => (
                  <Chip key={k} on={filter === k} count={counts[k]} onClick={() => setFilter(k as RoomFilter)}>
                    {label}
                  </Chip>
                ))}
              </div>
              <ul className="k-rows rm-rows">
                {shown.map((r) => (
                  <RoomRow key={r.id} room={r} on={r.id === open?.id} onPick={() => setPicked(r.id)} menu={null} />
                ))}
              </ul>
            </Page>
          </Pane>
          <Pane kind="main" showing={open?.id ?? null} label="The room that is open">
            <Page layout="side" className="rm">
              {open ? <SideSummary key={open.id} room={open} onChanged={reload} /> : null}
            </Page>
          </Pane>
        </Panes>
        {sheets}
      </>
    );
  }

  return (
    <Page layout={layout} narrow className="rm">
      {title}
      {strip}
      {now}
      <div className="rx-h">
        <h2>Your rooms</h2>
      </div>
      <CountSwitch label="Which rooms" value={filter} onChange={setFilter} options={switchOptions} />
      {layout === "upright" ? (
        <div className="k-cols rm-grid">
          {shown.map((r) => (
            <RoomCard key={r.id} room={r} layout={layout} onChanged={reload} />
          ))}
        </div>
      ) : (
        <ul className="k-rows rm-rows">
          {shown.map((r) => (
            <RoomRow key={r.id} room={r} menu={menuFor(r)} />
          ))}
        </ul>
      )}
      <DockedBar>
        <Button variant="p" full icon="plus" disabled={held !== null} onClick={() => setMaking(true)}>
          New room
        </Button>
      </DockedBar>
      {sheets}
    </Page>
  );
}
